import BugReportIcon from '@mui/icons-material/BugReport';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import blue from '@mui/material/colors/blue';
import blueGrey from '@mui/material/colors/blueGrey';
import green from '@mui/material/colors/green';
import grey from '@mui/material/colors/grey';
import red from '@mui/material/colors/red';
import Grid from '@mui/material/Grid/Grid';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material/SvgIcon/SvgIcon';
import Typography from '@mui/material/Typography';
import React, { ReactNode } from 'react';

import useTimer from '~hooks/useTimer';

import StatusSelector from './StatusSelector';

export const StatusColor = {
  blueGrey: blueGrey[800],
  red: red[800],
  grey: grey[600],
  blue: blue[800],
  green: green[800],
} as const;

export type StatusColor = typeof StatusColor[keyof typeof StatusColor];

type Props = {
  statusList: string[];
  statusIcon?: OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
  status?: string;
  statusColor?: StatusColor;
  statusTimestamp?: string;
  statsSection?: ReactNode;
  actionsSection?: ReactNode;
  subActionsSection?: ReactNode;
  isStateChangeDisabled?: boolean;
  setAgentState: (status: string) => void;
  // Hack to support 1 for 1 look and feel for asterisk dialler from svelte to react
  replaceStatusSelector?: ReactNode;
};

const textColor = '#ffffff';

export const AgentStatusBarBase = ({
  statusList,
  statusIcon,
  status,
  statusColor,
  statusTimestamp,
  statsSection,
  actionsSection,
  subActionsSection,
  isStateChangeDisabled,
  setAgentState,
  replaceStatusSelector,
}: Props) => {
  const sTimer = useTimer(statusTimestamp ? new Date(statusTimestamp) : undefined);
  const SIcon = statusIcon || BugReportIcon;
  const sText = status || 'Unknown';
  const sBgColor = statusColor || StatusColor.blueGrey;

  return (
    <Card sx={{ backgroundColor: sBgColor, width: '100%', position: 'relative', overflow: 'initial' }} elevation={3}>
      <CardContent
        sx={{
          'display': 'flex',
          'justifyContent': 'space-between',
          'alignItems': 'center',
          'padding': 0,
          ':last-of-type': {
            paddingBottom: 0,
          },
        }}>
        <Grid container spacing={0}>
          <Grid
            sx={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              paddingTop: 1,
              paddingLeft: 2,
              paddingBottom: 1,
            }}
            item
            xs={6}>
            <SIcon sx={{ color: textColor, marginRight: 2 }} fontSize='large' />

            <span>
              <Typography color={textColor} variant='h6'>
                {sText}
              </Typography>

              <Typography color={textColor} variant='body2'>
                {sTimer}
              </Typography>
            </span>
          </Grid>

          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              paddingTop: 1,
              paddingRight: 2,
              paddingBottom: 1,
            }}
            item
            xs={6}>
            {actionsSection}

            {replaceStatusSelector}
            {!replaceStatusSelector && (
              <StatusSelector onChange={setAgentState} items={statusList} disabled={Boolean(isStateChangeDisabled)} />
            )}
          </Grid>
        </Grid>
      </CardContent>

      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '0 0 8px 8px',
          padding: '4px 16px',
          minHeight: 32,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>{statsSection}</Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
          {subActionsSection}
        </Box>
      </Box>
    </Card>
  );
};

export default AgentStatusBarBase;
